import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from "@/integrations/supabase/client"; 
import { checkPaymentStatus, updatePaymentStatus } from '@/services/mollieService';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';

export default function MollieCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [params, setParams] = useState<Record<string, string>>({});
  const [fullUrl, setFullUrl] = useState<string>('');
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [paymentType, setPaymentType] = useState<string | null>(null);

  useEffect(() => {
    const checkPayment = async () => {
      const urlPaymentId = searchParams.get('payment_id');
      const storedPaymentId = localStorage.getItem('mollie_payment_id') || sessionStorage.getItem('mollie_payment_id');
      const paymentIdToCheck = urlPaymentId || storedPaymentId;

      if (!paymentIdToCheck) {
        setIsChecking(false);
        setPaymentStatus('not_found');
        return;
      }

      setPaymentId(paymentIdToCheck);

      const paramsObj: Record<string, string> = {};
      searchParams.forEach((value, key) => { paramsObj[key] = value; });
      setParams(paramsObj);
      setFullUrl(window.location.href);

      try {
        const statusResult = await checkPaymentStatus(paymentIdToCheck);
        
        if (statusResult.success && statusResult.status) {
          setPaymentStatus(statusResult.status);
          
          // 1. D'abord on met à jour le statut en base (ex: passage de 'open' à 'paid')
          const updateResult = await updatePaymentStatus(paymentIdToCheck, statusResult.status);
          
          if (!updateResult.success) {
            setPaymentStatus('error');
            toast.error(updateResult.error || 'Failed to process payment');
            setIsChecking(false);
            return;
          }
          
          if (updateResult.paymentType) {
            setPaymentType(updateResult.paymentType);
          }

          // Si le paiement est validé (PAID)
          if (statusResult.status === 'paid') {
            
            // ============================================================
            // 🚀 GÉNÉRATION FACTURE SÉQUENTIELLE
            // ============================================================
            try {
              const { data: { user } } = await supabase.auth.getUser();

              // 2. COMPTER UNIQUEMENT LES PAIEMENTS "PAID"
              // C'est cette ligne qui garantit que le numéro de facture suit les paiements réussis
              const { count } = await supabase
                .from('payments')
                .select('*', { count: 'exact', head: true })
                .eq('status', 'paid'); // <--- ICI : On filtre uniquement les payés
              
              // Le paiement actuel vient d'être passé en 'paid', il est donc inclus dans le 'count'.
              const seqNumber = (count || 0); 
              const formattedNumber = String(seqNumber).padStart(3, '0'); // ex: "005"
              const invoiceCode = `IK-2026-${formattedNumber}`;

              // 3. Récupérer les données
              const { data: paymentData, error: paymentError } = await supabase
                .from('payments') 
                .select(`
                  *,
                  profiles:user_id ( first_name, last_name, display_name ) 
                `) 
                .eq('mollie_payment_id', paymentIdToCheck)
                .single();

              if (paymentData && !paymentError) {
                
                const formatProductName = (type: string) => {
                    if (!type) return "Abonnement IndieKeys";
                    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
                };

                // --- CONSTRUCTION INTELLIGENTE DU NOM ---
                let clientName = "Client IndieKeys";
                const userEmail = user?.email || "";
                const profile = paymentData.profiles;

                if (profile?.first_name || profile?.last_name) {
                    clientName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
                } else if (profile?.display_name) {
                    clientName = profile.display_name;
                } else if (user?.user_metadata?.full_name) {
                    clientName = user.user_metadata.full_name;
                } else if (userEmail) {
                    clientName = userEmail;
                }

                // --- ADRESSE (Eviter doublon email) ---
                let addressLine1 = userEmail;
                if (clientName === userEmail) {
                    addressLine1 = ""; 
                }

                const invoicePayload = {
                  invoiceNum: invoiceCode,
                  date: new Date().toLocaleDateString('fr-FR'),
                  customer: { 
                    name: clientName, 
                    address1: addressLine1, 
                    address2: "" 
                  },
                  items: [
                    { 
                      name: formatProductName(paymentData.payment_type), 
                      qty: 1, 
                      price: `${Number(paymentData.amount).toFixed(2)} €`, 
                      total: `${Number(paymentData.amount).toFixed(2)} €` 
                    }
                  ],
                  totals: { 
                    subtotal: `${Number(paymentData.amount).toFixed(2)} €`, 
                    vat: "0.00 €", 
                    total: `${Number(paymentData.amount).toFixed(2)} €` 
                  }
                };

                // Appel Edge Function
                const { data: invoiceRes, error: invoiceError } = await supabase.functions.invoke('generate-invoice', {
                  body: { invoiceData: invoicePayload }
                });

                if (!invoiceError && invoiceRes?.success) {
                  console.log("Facture générée : ", invoiceRes.url);
                  // Sauvegarde de l'URL (Note: C'est un lien signé temporaire ici)
                  await supabase
                    .from('payments')
                    .update({ invoice_url: invoiceRes.url })
                    .eq('id', paymentData.id);
                  toast.success("Facture disponible !");
                } else {
                  console.error("Erreur génération PDF:", invoiceError);
                }
              }
            } catch (err) {
              console.error("Crash bloc facture:", err);
            }
            // ============================================================

            const storedData = localStorage.getItem('mollie_payment_data');
            let storedPaymentType = null;
            if (storedData) {
              try {
                const parsed = JSON.parse(storedData);
                storedPaymentType = parsed.paymentType;
              } catch (e) {}
            }
            const finalPaymentType = updateResult.paymentType || storedPaymentType;
             
            if (finalPaymentType?.startsWith('premium_')) {
              toast.success('Payment successful! Your premium subscription has been activated.');
            } else if (finalPaymentType === 'press_kit') {
              toast.success('Payment successful! Your press kit is being generated.');
            } else if (finalPaymentType?.includes('boost')) {
              toast.success('Payment successful! Your boost has been activated.');
            } else {
              toast.success('Payment successful!');
            }
            
            localStorage.removeItem('mollie_payment_id');
            localStorage.removeItem('mollie_payment_data');
            
            const redirectPath = finalPaymentType?.startsWith('premium_') 
              ? '/subscriptions' 
              : finalPaymentType === 'press_kit' 
                ? '/dashboard'
                : '/subscriptions';
            
            setTimeout(() => {
              navigate(redirectPath);
            }, 3000);

          } else if (['failed', 'expired', 'canceled'].includes(statusResult.status)) {
            toast.error('Payment was not completed. Please try again.');
          } else if (['pending', 'open'].includes(statusResult.status)) {
            toast.info('Payment is still pending. Please complete the payment.');
          }
        } else {
          setPaymentStatus('error');
          toast.error(statusResult.error || 'Failed to check payment status');
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        setPaymentStatus('error');
        toast.error('An error occurred while checking payment status');
      } finally {
        setIsChecking(false);
      }
    };

    checkPayment();
  }, [searchParams, navigate]);

  const hasParams = Object.keys(params).length > 0;

  const getStatusIcon = () => {
    if (isChecking) {
      return <Loader2 className="w-8 h-8 animate-spin text-primary" />;
    }
    
    switch (paymentStatus) {
      case 'paid':
        return <CheckCircle className="w-8 h-8 text-green-500" />;
      case 'failed':
      case 'expired':
      case 'canceled':
      case 'error':
        return <XCircle className="w-8 h-8 text-red-500" />;
      case 'pending':
      case 'open':
        return <Clock className="w-8 h-8 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusMessage = () => {
    if (isChecking) {
      return 'Checking payment status...';
    }
    
    const getPaymentTypeMessage = (type: string | null) => {
      if (!type) return '';
      
      if (type.startsWith('premium_')) {
        return 'Your premium subscription has been activated.';
      } else if (type === 'press_kit') {
        return 'Your press kit is being generated.';
      } else if (type.includes('boost')) {
        return 'Your boost has been activated.';
      }
      return '';
    };
    
    switch (paymentStatus) {
      case 'paid':
        const typeMessage = getPaymentTypeMessage(paymentType);
        return typeMessage 
          ? `Payment successful! ${typeMessage}`
          : 'Payment successful!';
      case 'failed':
        return 'Payment failed. Please try again.';
      case 'expired':
        return 'Payment expired. Please create a new payment.';
      case 'canceled':
        return 'Payment was canceled.';
      case 'pending':
      case 'open':
        return 'Payment is still pending. Please complete the payment.';
      case 'not_found':
        return 'Payment ID not found.';
      case 'error':
        return 'An error occurred while checking payment status.';
      default:
        return 'Unknown payment status.';
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <Card>
          <CardHeader>
            <CardTitle>Payment Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center py-6">
              {getStatusIcon()}
              <h3 className="text-xl font-semibold mt-4 mb-2">
                {getStatusMessage()}
              </h3>
              {paymentId && (
                <p className="text-sm text-muted-foreground">
                  Payment ID: <code className="bg-muted px-2 py-1 rounded">{paymentId}</code>
                </p>
              )}
              {paymentStatus === 'paid' && (
                <p className="text-sm text-muted-foreground mt-2">
                  Redirecting to subscriptions page...
                </p>
              )}
            </div>

            {import.meta.env.DEV && (
              <>
                <div className="pt-4 border-t">
                  <h3 className="font-semibold mb-2">Full URL:</h3>
                  <code className="block p-2 bg-muted rounded text-sm break-all">
                    {fullUrl || 'Loading...'}
                  </code>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">
                    URL Parameters ({Object.keys(params).length}):
                  </h3>
                  {hasParams ? (
                    <div className="space-y-2">
                      {Object.entries(params).map(([key, value]) => (
                        <div key={key} className="flex items-start gap-2 p-2 bg-muted rounded">
                          <Badge variant="outline" className="min-w-fit">
                            {key}
                          </Badge>
                          <code className="flex-1 text-sm break-all">{value}</code>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 bg-muted rounded text-sm text-muted-foreground">
                      No parameters in URL
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="pt-4 border-t flex gap-4 justify-center">
              <Button variant="outline" onClick={() => navigate('/subscriptions')}>
                Back to Subscriptions
              </Button>
              {paymentStatus !== 'paid' && paymentStatus !== 'pending' && paymentStatus !== 'open' && (
                <Button onClick={() => navigate('/subscriptions')}>
                  Try Again
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}